import React, { useEffect, useState } from 'react'

import { Skeleton } from '@material-ui/lab'
import PropTypes from 'prop-types'
import ReCAPTCHA from 'react-google-recaptcha'

import useSnackbar from 'hooks/useSnackbar'

import { getGoogleRecaptchaToken } from 'service/env'

// 150 x 10 = 1,5 segundo de tolerância
const READY_POLL_INTERVAL = 150
const READY_MAX_ATTEMPTS = 10

const JourneyRecaptcha = ({ recaptchaRef, onChange, onExpired, onErrored }) => {
  const isRecaptchaReady = () =>
    Boolean(window.grecaptcha && window.grecaptcha.render)

  const [ready, setReady] = useState(isRecaptchaReady)
  const [rendered, setRendered] = useState(isRecaptchaReady)

  const snackbar = useSnackbar()

  const getScriptUrl = () => {
    const useRecaptchaNet = window.recaptchaOptions?.useRecaptchaNet
    const hostname = useRecaptchaNet ? 'recaptcha.net' : 'www.google.com'

    return `https://${hostname}/recaptcha/api.js?render=explicit`
  }

  const handleLoadError = () =>
    snackbar.open({
      message:
        'Erro ao carregar o reCAPTCHA. Recarregue a página e tente novamente.',
      variant: 'error',
    })

  useEffect(() => {
    if (ready) return undefined

    let active = true

    const confirmReady = () => {
      if (!active) return

      if (window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          if (active) setReady(true)
        })
      } else {
        setReady(true)
      }
    }

    if (isRecaptchaReady()) {
      confirmReady()
      setRendered(true)
      return undefined
    }

    let intervalId

    const handleScriptError = () => {
      clearInterval(intervalId)
      handleLoadError()
    }

    const handleScriptLoad = () => {
      if (isRecaptchaReady()) {
        clearInterval(intervalId)
        confirmReady()
        setRendered(true)
      }
    }

    const existingScript = document.querySelector(
      'script[src*="recaptcha/api.js"]',
    )

    if (!existingScript) {
      const script = document.createElement('script')
      script.src = getScriptUrl()
      script.async = true
      script.defer = true
      script.onload = handleScriptLoad
      script.onerror = handleScriptError
      document.body.appendChild(script)
    }

    let attempts = 0

    intervalId = setInterval(() => {
      attempts += 1

      if (isRecaptchaReady()) {
        clearInterval(intervalId)
        confirmReady()
        setRendered(true)
      } else if (attempts >= READY_MAX_ATTEMPTS) {
        clearInterval(intervalId)
        handleLoadError()
      }
    }, READY_POLL_INTERVAL)

    return () => {
      active = false
      clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  return (
    <>
      {!rendered ? (
        <Skeleton variant="rect" width={304} height={78} />
      ) : (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={getGoogleRecaptchaToken()}
          onChange={onChange}
          onExpired={onExpired}
          onErrored={onErrored}
        />
      )}
    </>
  )
}

JourneyRecaptcha.propTypes = {
  recaptchaRef: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onExpired: PropTypes.func,
  onErrored: PropTypes.func,
}

export default JourneyRecaptcha
